/**
 * Editor Toolbar — Device preview sizes, save status, chat toggle, versions, deploy, back navigation.
 */

import { useState } from 'react';
import {
  ArrowLeft,
  Monitor,
  Tablet,
  Smartphone,
  MessageSquare,
  Image,
  Save,
  Loader2,
  Download,
  Rocket,
  Maximize2,
} from 'lucide-react';
import { generatorHelpers } from '../../lib/api';
import { toast } from 'sonner';

interface ToolbarProps {
  device: 'desktop' | 'tablet' | 'mobile';
  onDeviceChange: (device: 'desktop' | 'tablet' | 'mobile') => void;
  onToggleChat: () => void;
  onToggleMedia: () => void;
  onToggleVersions: () => void;
  isChatOpen: boolean;
  isVersionsOpen: boolean;
  isSaving: boolean;
  projectId: string;
  jobId: string;
  onBack: () => void;
  onToggleFullPreview?: () => void;
}

export function Toolbar({
  device,
  onDeviceChange,
  onToggleChat,
  onToggleMedia,
  onToggleVersions,
  isChatOpen,
  isVersionsOpen,
  isSaving,
  projectId,
  jobId,
  onBack,
  onToggleFullPreview,
}: ToolbarProps) {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadZip = async () => {
    setDownloading(true);
    try {
      const res = await generatorHelpers.exportZip(projectId, jobId);
      const blob = res.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Extract filename from Content-Disposition or use fallback
      const disposition = res.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
      a.download = filenameMatch?.[1] || `website-${jobId.slice(-8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch {
      toast.error('Failed to download ZIP');
    } finally {
      setDownloading(false);
    }
  };
  const devices = [
    { id: 'desktop' as const, icon: Monitor, label: 'Desktop' },
    { id: 'tablet' as const, icon: Tablet, label: 'Tablet' },
    { id: 'mobile' as const, icon: Smartphone, label: 'Mobile' },
  ];

  return (
    <div className="flex h-12 items-center justify-between border-b bg-white px-4 shadow-sm">
      {/* Left: Back + title */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-600 transition-colors hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>
        <div className="h-5 w-px bg-gray-200" />
        <span className="text-sm font-medium text-gray-800">Visual Editor</span>
      </div>

      {/* Center: Device switcher */}
      <div className="flex items-center gap-1 rounded-lg bg-gray-100 p-0.5">
        {devices.map(({ id, icon: Icon, label }) => (
          <button
            key={id}
            onClick={() => onDeviceChange(id)}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
              device === id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            title={label}
          >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Save indicator */}
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          {isSaving ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-3 w-3" />
              Auto-saved
            </>
          )}
        </div>

        <div className="h-5 w-px bg-gray-200" />

        {/* Media picker */}
        <button
          onClick={onToggleMedia}
          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
          title="Media library"
        >
          <Image className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Media</span>
        </button>

        {/* Full preview */}
        {onToggleFullPreview && (
          <button
            onClick={onToggleFullPreview}
            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100"
            title="Full-screen preview"
          >
            <Maximize2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Preview</span>
          </button>
        )}

        {/* Download ZIP */}
        <button
          onClick={handleDownloadZip}
          disabled={downloading}
          className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50"
          title="Download as ZIP"
        >
          {downloading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
          <span className="hidden sm:inline">Export</span>
        </button>

        {/* Chat toggle */}
        <button
          onClick={onToggleChat}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            isChatOpen ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'
          }`}
          title="AI Chat"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">AI Chat</span>
        </button>

        {/* Versions toggle */}
        <button
          onClick={onToggleVersions}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            isVersionsOpen ? 'bg-purple-50 text-purple-600' : 'text-gray-600 hover:bg-gray-100'
          }`}
          title="Versions & Deploy"
        >
          <Rocket className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Deploy</span>
        </button>
      </div>
    </div>
  );
}
